<template>
  <div class="menu-org-section">
    <div
      v-if="isMenuExpanded"
      class="menu-org-header isMenuExpanded"
      :class="{ 'no-items': hasNoItems }"
      role="button"
      tabindex="0"
      @click="hasNoItems ? goToHome($event) : toggleCollapsed()"
    >
      <a class="menu-name" :class="{ icon: !isMenuExpanded }">
        <span class="name">{{ orgName }}</span>
        <div class="space" />
        <button class="settings-icon-btn" @click.stop="goToHome($event)">
          <HomeIcon class="settings-icon" />
        </button>
        <ChevronDownIcon
          v-if="!hasNoItems"
          class="chevron"
          :class="{ 'chevron-collapsed': isCollapsed }"
        />
      </a>
    </div>
    <div
      v-show="!effectiveIsCollapsed && !hasNoItems"
      class="menu-org-section-list"
    >
      <!-- Single list where the app screens (Tasks/Pages, ...) can be
           interleaved with the pinned items. -->
      <BaseReorderableList
        :data="draggableMergedItems"
        tag="div"
        root-class="menu-primary menu-primary-list"
        :item-key="(item: ApiMenuItem) => getMenuItemKey(item)"
        :disabled="isSaving"
        @change="onReorderMerged"
        @drag-start="$emit('dragStart')"
        @drag-end="$emit('dragEnd')"
      >
        <template #item="{ item }">
          <MenuItem :route="item" :org-id="orgId" />
        </template>
      </BaseReorderableList>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue";

import type { MenuItem as ApiMenuItem, OrgId } from "@ctrl/controller-rpc";

import BaseReorderableList from "@/components/base/BaseReorderableList.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import HomeIcon from "@/components/icons/HomeIcon.vue";
import * as inject from "@/components/inject";
import MenuItem from "@/components/menu/MenuItem.vue";
import { getMenuItemKey } from "@/components/menu/menuItems";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

export default defineComponent({
  name: "MenuOrgSection",
  components: {
    BaseReorderableList,
    ChevronDownIcon,
    HomeIcon,
    MenuItem,
  },
  props: {
    orgId: { type: String as PropType<OrgId>, required: true },
    // App screens (Tasks, Pages, ...) and pins merged into a single ordered
    // list (see useMenuSections).
    mergedItems: { type: Array as PropType<ApiMenuItem[]>, required: true },
    // Owned by MenuPrimary (one persisted record for all sections);
    // toggling emits `toggleCollapsed`.
    isCollapsed: { type: Boolean, required: true },
    isSaving: { type: Boolean, default: false },
  },
  emits: {
    reorderMerged: (_items: ApiMenuItem[]) => true,
    toggleCollapsed: () => true,
    dragStart: () => true,
    dragEnd: () => true,
  },
  setup(props, { emit }) {
    const router = inject.router();
    const isMenuExpanded = inject.menu().isExpanded;
    const profile = inject.profile();

    const orgName = computed(() => {
      const org = profile.value.organizations.find((o) => o.id === props.orgId);
      return org?.name ?? "Organization";
    });

    const isPersonalOrg = computed(
      () => props.orgId === profile.value.user.personal.organizationId
    );

    const effectiveIsCollapsed = computed(
      () => isMenuExpanded.value && props.isCollapsed
    );

    const toggleCollapsed = () => emit("toggleCollapsed");

    const draggableMergedItems = ref<Array<{ items: ApiMenuItem[] }>>([
      { items: [] },
    ]);

    const hasNoItems = computed(
      () => draggableMergedItems.value[0].items.length === 0
    );

    watch(
      () => props.mergedItems,
      (items) => {
        draggableMergedItems.value[0].items = [...items];
      },
      { immediate: true }
    );

    const onReorderMerged = () => {
      emit("reorderMerged", draggableMergedItems.value[0].items);
    };

    const goToHome = (event?: MouseEvent) => {
      if (isPersonalOrg.value) {
        // The personal org has no organization screen worth opening: go to
        // its single workspace instead.
        void navigateTo(
          RouteNames.Workspace,
          router,
          {
            params: {
              orgId: props.orgId,
              workspaceId: profile.value.user.personal.workspaceId,
            },
          },
          event
        );
        return;
      }
      void navigateTo(
        RouteNames.Organization,
        router,
        { params: { orgId: props.orgId } },
        event
      );
    };

    return {
      draggableMergedItems,
      effectiveIsCollapsed,
      getMenuItemKey,
      goToHome,
      hasNoItems,
      isMenuExpanded,
      onReorderMerged,
      orgName,
      toggleCollapsed,
    };
  },
});
</script>

<style scoped lang="scss">
.menu-org-section {
  width: 100%;
  min-width: 0;

  :deep(.reorderable-section) {
    margin-bottom: 0;
  }

  .menu-org-section-list {
    padding-bottom: 8px;
  }
}

.menu-org-header {
  cursor: default;
  user-select: none;
  display: flex;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  justify-content: center;
  align-items: center;
  margin: 4px 0;
  padding: 6px 0;
  box-sizing: border-box;
  outline: none;

  .menu-name {
    display: flex;
    align-items: center;
    text-decoration: none;
    width: 100%;

    span.name {
      display: none;
      @extend %font-caption-regular;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      @include themed() {
        color: t($grey-40);
      }
    }

    .space {
      flex: 1;
    }
  }

  &.isMenuExpanded {
    width: calc(100% - 12px);
    margin: 1px 6px;
    padding: 5px 12px;
    justify-content: start;
    border-radius: $border-radius-m;
    box-sizing: border-box;

    &:hover {
      @include themed() {
        background: t($bg-menu-hover);
      }
    }

    .menu-name {
      .org-icon {
        width: 18px;
        height: 18px;
      }

      span.name {
        display: inline-block;
      }
    }
  }
}

.settings-icon-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  opacity: 0;
  transition: opacity 100ms ease-out;

  .settings-icon {
    width: 14px;
    height: 14px;

    :deep(.stroke) {
      @include themed() {
        stroke: t($grey-30);
      }
    }
  }

  &:hover .settings-icon {
    :deep(.stroke) {
      @include themed() {
        stroke: t($grey-60);
      }
    }
  }
}

.menu-org-header:hover .settings-icon-btn {
  opacity: 1;
}

.chevron {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-inline-end: 0px;
  margin-inline-start: 12px;
  transition: transform 150ms ease-out;

  :deep(.fill) {
    @include themed() {
      fill: t($grey-30);
    }
  }

  &.chevron-collapsed {
    transform: rotate(-90deg);
  }
}
</style>
